/**
 * DONATION HELPERS
 * 
 * Utility functions for formatting and validating donations.
 * 
 * @module Donations/Utils
 */

import { Donation, DonationCurrency } from '../types/donation.types';
import { CURRENCIES } from '../constants/donation.constants';

/**
 * Format amount with currency symbol
 */
export const formatAmount = (amount: number, currency: DonationCurrency): string => {
    const currencyInfo = CURRENCIES.find(c => c.value === currency);
    const symbol = currencyInfo?.symbol || currency;
    return `${symbol} ${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/**
 * Format a date for display
 */
export const formatDate = (date: Date | string): string => {
    if (!date) return '';
    try {
        const d = typeof date === 'string' ? new Date(date) : date;
        return d.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    } catch {
        return '';
    }
};

/**
 * Get status label and color
 */
export const getStatusInfo = (status: string): { label: string; color: string } => {
    const statusMap: Record<string, { label: string; color: string }> = {
        pending: { label: 'Pending', color: '#F59E0B' },
        completed: { label: 'Completed', color: '#10B981' },
        failed: { label: 'Failed', color: '#EF4444' },
        refunded: { label: 'Refunded', color: '#6B7280' }
    };
    return statusMap[status] || { label: status, color: '#6B7280' };
};

/**
 * Get category label
 */
export const getCategoryLabel = (category: string): string => {
    const categoryMap: Record<string, string> = {
        radio_programs: 'Radio Programs',
        research: 'Research & Data',
        governance: 'Governance & Accountability',
        education: 'Education',
        health: 'Health',
        emergency: 'Emergency Response',
        general: 'General Fund'
    };
    return categoryMap[category] || category;
};

/**
 * Get method label
 */
export const getMethodLabel = (method: string): string => {
    const methodMap: Record<string, string> = {
        mobile_money: 'Mobile Money',
        bank_transfer: 'Bank Transfer',
        credit_card: 'Credit / Debit Card',
        paypal: 'PayPal',
        stripe: 'Stripe',
        crypto: 'Cryptocurrency'
    };
    return methodMap[method] || method;
};

/**
 * Validate donation form data
 */
export const validateDonation = (data: any): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];

    if (!data.donorName?.trim()) {
        errors.push('Donor name is required');
    }

    if (!data.donorEmail?.trim()) {
        errors.push('Donor email is required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.donorEmail)) {
        errors.push('Invalid email format');
    }

    if (!data.amount || data.amount <= 0) {
        errors.push('Amount must be greater than 0');
    }

    if (!data.category) {
        errors.push('Category is required');
    }

    if (!data.method) {
        errors.push('Payment method is required');
    }

    if (data.method === 'mobile_money' && !data.mobileNumber?.trim()) {
        errors.push('Mobile number is required for Mobile Money');
    }

    return {
        valid: errors.length === 0,
        errors
    };
};
